import {Color} from '@benchling/nucleus-ui';
import {Box, Flex, Group, Stack, Text} from '@mantine/core';
import React from 'react';

import * as AuditActions from 'audit/AuditActions';
import {EXPORT_AUDIT_LOG_DISPLAY_STRING} from 'audit/AuditConstants';
import BenchlingConfig from 'BenchlingConfig';
import {pluralize} from 'BenchlingUtil';
import {StructuredTableNode} from 'graphql-client/__generated/graphqlGlobalTypes';
import {useSkippableBenchlingQuery, QueryResultType} from 'graphql-client/BenchlingQuery';
import {DATA_MOD_SUMMARY_BY_TABLE_QUERY} from 'notebook/structured-tables/DataModificationSummaryQuery';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Popover from 'ui-library/floating-ui/Popover';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import BenchlingDuotoneIcon from 'ui-library/icons/BenchlingDuotoneIcon';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

export function renderFooterDataModSummary(params: {structuredTable: {apiId?: string}}): JSX.Element | null {
  if (!params.structuredTable.apiId) {
    return null;
  }
  return <FooterDataModSummary tableApiId={params.structuredTable.apiId} />;
}

export default function FooterDataModSummary({tableApiId}: {tableApiId: string | undefined}): JSX.Element {
  const result = useSkippableBenchlingQuery<{structuredTable: StructuredTableNode}>(
    DATA_MOD_SUMMARY_BY_TABLE_QUERY,
    {
      variables: {id: tableApiId},
      skip: !tableApiId || !BenchlingConfig.ELN_ENABLE_TABLE_DATA_MODIFICATION_SUMMARY,
      fetchPolicy: 'cache-first',
    }
  );

  if (!tableApiId || !BenchlingConfig.ELN_ENABLE_TABLE_DATA_MODIFICATION_SUMMARY) {
    return <></>;
  }

  const error = result.type === QueryResultType.ERROR ? 'Unable to retrieve data modifications' : null;
  const isLoading = result.type === QueryResultType.PENDING && !error;
  const dataModsSummary =
    result.type === QueryResultType.SUCCESS
      ? (result.data?.structuredTable?.dataModificationSummary ?? null)
      : null;

  const totalDataMods = dataModsSummary
    ? dataModsSummary.numRowsDeleted +
      dataModsSummary.numCellsUpdated +
      dataModsSummary.numAddedRowsSubmitted +
      dataModsSummary.numColumnsAdded +
      dataModsSummary.numColumnsDeleted
    : 0;

  return (
    <>
      {isLoading && (
        <Tooltip text="Loading data modifications">
          <BenchlingDuotoneIcon
            icon="spinner-third"
            primaryColor={Color.GRAY_600}
            secondaryColor={Color.GRAY_200}
            spin={true}
          />
        </Tooltip>
      )}
      {error && (
        <Tooltip text="Unable to retrieve data modifications">
          <BenchlingIcon icon="audit-trail" color="red" />
        </Tooltip>
      )}
      {!isLoading && !error && dataModsSummary != null && totalDataMods > 0 && (
        <Popover
          renderContent={() => (
            <Stack align="flex-start" p="sm">
              {dataModsSummary.numRowsDeleted > 0 && (
                <Text>
                  {dataModsSummary.numRowsDeleted}{' '}
                  {pluralize(dataModsSummary.numRowsDeleted, 'submitted row', 'submitted rows')} deleted
                </Text>
              )}
              {dataModsSummary.numCellsUpdated > 0 && (
                <Text>
                  {dataModsSummary.numCellsUpdated}{' '}
                  {pluralize(dataModsSummary.numCellsUpdated, 'cell', 'cells')} updated
                </Text>
              )}
              {dataModsSummary.numAddedRowsSubmitted > 0 && (
                <Text>
                  {dataModsSummary.numAddedRowsSubmitted}{' '}
                  {pluralize(dataModsSummary.numAddedRowsSubmitted, 'row', 'rows')} added
                </Text>
              )}
              {dataModsSummary.numColumnsAdded > 0 && (
                <Text>
                  {dataModsSummary.numColumnsAdded}{' '}
                  {pluralize(dataModsSummary.numColumnsAdded, 'column', 'columns')} added
                </Text>
              )}
              {dataModsSummary.numColumnsDeleted > 0 && (
                <Text>
                  {dataModsSummary.numColumnsDeleted}{' '}
                  {pluralize(dataModsSummary.numColumnsDeleted, 'column', 'columns')} deleted
                </Text>
              )}
              {/* Override the default margin of the button so that we can align it with the text */}
              {tableApiId && (
                <Box sx={{margin: '-6px -12px'}}>
                  <Button
                    buttonStyle={ButtonStyle.TEXT}
                    size={ButtonSize.SMALL}
                    onClick={() => {
                      AuditActions.exportAuditLogs([tableApiId]);
                    }}
                  >
                    <Flex align="center" gap="sm">
                      {EXPORT_AUDIT_LOG_DISPLAY_STRING} <BenchlingIcon icon="open" />
                    </Flex>
                  </Button>
                </Box>
              )}
            </Stack>
          )}
        >
          <Group spacing="xs">
            <BenchlingIcon icon="audit-trail" />
            <Text>
              {totalDataMods} {pluralize(totalDataMods, 'data modification', 'data modifications')}
            </Text>
          </Group>
        </Popover>
      )}
    </>
  );
}
